/**
 * BetaApplicationCard Component
 *
 * Administrative interface card for beta application management dashboard display.
 * Shows application statistics and navigation to detailed beta application management.
 */

import React from 'react';
import { Card, CardHeader, CardBody, CardFooter, Button, Chip } from '@enfomentis/ui';
import { Link as RouterLink } from 'react-router-dom';
import { FileText } from 'lucide-react';
import type { BetaApplicationStatus } from '@/types/supabase/BetaApplication';

/**
 * Props for BetaApplicationCard component
 */
export interface BetaApplicationCardProps {
  /** Total number of beta applications */
  betaApplicationCount: number;
  /** Application status counts */
  statusCounts: Record<BetaApplicationStatus, number>;
  /** Optional title override */
  title?: string;
}

/**
 * Beta Application Card Component
 *
 * Features:
 * - Real-time beta application statistics
 * - Status breakdown with color-coded chips
 * - Responsive design matching admin interface patterns
 * - Navigation integration to detailed application management
 * - Consistent styling with other dashboard cards
 *
 * Usage:
 * ```tsx
 * <BetaApplicationCard
 *   betaApplicationCount={applications.length}
 *   statusCounts={counts}
 * />
 * ```
 */
export const BetaApplicationCard: React.FC<BetaApplicationCardProps> = ({
  betaApplicationCount,
  statusCounts,
  title = "Beta Applications"
}) => {
  return (
    <Card shadow="sm" className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary bg-opacity-10">
            <FileText className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h4 className="font-semibold text-text-primary">{title}</h4>
            <div className="flex gap-2 flex-wrap">
              {betaApplicationCount > 0 ? (
                <>
                  {statusCounts.pending > 0 && (
                    <Chip size="sm" color="warning" variant="flat">
                      {statusCounts.pending} Pending
                    </Chip>
                  )}
                  {statusCounts.approved > 0 && (
                    <Chip size="sm" color="success" variant="flat">
                      {statusCounts.approved} Approved
                    </Chip>
                  )}
                  {statusCounts.rejected > 0 && (
                    <Chip size="sm" color="danger" variant="flat">
                      {statusCounts.rejected} Rejected
                    </Chip>
                  )}
                </>
              ) : (
                <Chip size="sm" color="primary" variant="flat">
                  Available
                </Chip>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardBody className="pt-0">
        <p className="text-sm text-text-secondary mb-3">
          {`Review and manage beta tester applications${betaApplicationCount > 0 ? ` • ${statusCounts.pending} pending review` : ''}`}
        </p>
      </CardBody>

      <CardFooter>
        <RouterLink to="/admin/beta-applications" className="w-full">
          <Button
            color="primary"
            variant="flat"
            size="sm"
            className="w-full"
          >
            Access {title}
          </Button>
        </RouterLink>
      </CardFooter>
    </Card>
  );
};